// Must come first: it publishes bootstrap, DOMPurify, marked and axios as
// globals that the modules below (and a few template inline scripts) expect.
import { axios } from './vendor.js';

import { initializeChatPage } from './chat.js';
import { initializeFaq } from './faq.js';
import { initializePersonaPage } from './persona.js';
import { initHistory } from './history.js'; // Import history module
import { initializeContextPanel } from './context_panel.js';
import { initializeLogosSetupPage } from './logos_setup.js';
import { initializeContentPage, initializeSharedAnswers } from './content_page.js';
import { initAnswerCheckPopovers } from './answer_checks.js';
import { initErrorReporter } from './error_reporter.js';
import { initAccountStatusRefresh } from './account_status.js';
import { initializeControlArea } from './control.js';
import { getCsrfToken } from './csrf.js';
import { initSidebarLayout } from './sidebar_layout.js';
import { initSettingsShares, initShareModal } from './share.js';
import { initControlSharing } from './control_sharing.js';
import { initControlChecks } from './control_checks.js';

// Default timeout for ordinary (fast) API calls so a stalled request fails
// cleanly instead of hanging the browser forever. The long-running LLM calls
// (/ask, /api/conversation/ask) override this with a higher per-call timeout.
// No presence guard needed since vendor.js bundles axios in.
axios.defaults.timeout = 30000;

// One assignment covers every axios caller in the bundle and the two classic
// scripts that use the same global instance (manage_facets.js, the inline
// dashboard scripts) - so no mutating call has to remember the token itself
// (ADR-0049). Safe as a blanket default because every axios request in this
// app goes to a relative URL: the token never leaves our origin. The local
// Logos bridge is talked to with fetch (bridge_client.js), not axios, and
// keeps it that way for exactly this reason.
axios.defaults.headers.common['X-CSRFToken'] = getCsrfToken();

// ===== GENERAL INITIALIZERS =====

function initializeTooltips() {
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new bootstrap.Tooltip(tooltipTriggerEl);
    });
}

function handleCookieConsent() {
    const cookieBanner = document.getElementById('cookie-consent-banner');
    const acceptCookies = document.getElementById('accept-cookies');
    if (!cookieBanner || !acceptCookies) return;
    if (localStorage.getItem('cookie_consent') === 'true') {
        cookieBanner.style.display = 'none';
    }
    acceptCookies.addEventListener('click', () => {
        localStorage.setItem('cookie_consent', 'true');
        cookieBanner.style.display = 'none';
    });
}



// ===== DOMContentLoaded =====

document.addEventListener('DOMContentLoaded', () => {
    initializeTooltips();
    handleCookieConsent();
    // Every page, so not behind one of the dispatcher's element checks below:
    // an error can happen anywhere (ADR-0032).
    initErrorReporter();

    if (document.querySelector('.app-sidebar-resizer')) {
        initSidebarLayout();
    }
    if (document.querySelector('.faq-question')) {
        initializeFaq();
    }
    if (document.getElementById('start-button')) {
        initializePersonaPage();
    }
    if (document.getElementById('frageForm')) {
        initializeChatPage();
        initHistory(); // Initialize history module
    }
    if (document.getElementById('contextPanel')) {
        initializeContextPanel();
    }
    if (document.getElementById('logosSetupPage')) {
        initializeLogosSetupPage();
    }
    if (document.getElementById('controlArea')) {
        initializeControlArea();
    }
    if (document.getElementById('contentExampleAnswer')) {
        initializeContentPage(
            document.querySelectorAll('#contentExampleAnswer, .content-page__body')
        );
    }
    if (document.getElementById('shareModal')) {
        initShareModal();
    }
    if (document.getElementById('settingsShareList')) {
        initSettingsShares();
    }
    if (document.getElementById('controlSharing')) {
        initControlSharing();
    }
    if (document.getElementById('controlChecks')) {
        initControlChecks();
    }
    // Answers only: a shared question stays plain text (ADR-0084, FR-27).
    if (document.getElementById('sharedConversation')) {
        initializeSharedAnswers(
            document.querySelectorAll('#sharedConversation .bot-message .message-content')
        );
    }
    // Once per page, by delegation: the chat rebuilds its messages with
    // `innerHTML +=` (ADR-0085 D-13).
    if (document.getElementById('chatMessages') || document.getElementById('sharedConversation')) {
        initAnswerCheckPopovers();
    }
    // Every base_app page carries the settings modal, so this is keyed on the
    // modal rather than on any one page.
    if (document.getElementById('settingsModal')) {
        initAccountStatusRefresh();
    }
});


// The bundle announcing itself to the inline error reporter (ADR-0032, T-2).
//
// Module scope, not inside DOMContentLoaded, and that placement is the whole
// point: here the flag tests whether this script executed at all, which is the
// ADR-0043 failure — a script refused in transit. Inside the handler it would
// test event ordering instead and would stay unset on a page that finished
// loading before the bundle parsed.
//
// Keep this the last statement of the module.
window.__shepwiseReady = true;
